<template>
  <section>
    <div class="mar-b-xs">
      <span class="is-4 title ">
        Third Party Delivery
      </span>
    </div>

    <transition name="fade-zoom">
      <div>
        <b-field label="Order Service Providers">
          <div class="order-provider-services">
            <div
              v-for="storeService in orderProviderStoreServices"
              :key="storeService.id"
              :order-provider-store-service="storeService"
              class="pad has-background-white-bis has-border has-border-grey-lightest align-content-center"
            >
              <div class="is-flex justify-between">
                <div class="is-flex gap align-center">
                  <p class="subtitle mar-none is-inline-block">
                    {{ getOrderProviderServiceDescription(storeService.orderProviderServiceId) }}
                  </p>
                  <b-switch
                    :value="storeService.isActive"
                    class="left-label"
                    @input="toggleActive(storeService)"
                  />
                </div>
                <div class="is-flex gap align-center">
                  <b-button
                    icon-right="gears"
                    @click="openOrderProviderStoreServiceConfigurationModal(storeService)"
                  >
                    Configure
                  </b-button>
                  <b-button
                    outlined
                    icon-left="trash-alt"
                    type="is-danger is-light"
                    @click="confirmRemoveOrderProviderStoreService(storeService)"
                  />
                </div>
              </div>
            </div>
            <b-button
              icon-left="plus"
              type="is-primary is-light"
              outlined
              class="is-full-width is-full-height pad-lg"
              size="is-medium"
              @click="openOrderServiceProviderModal"
            >
              Order Provider Service
            </b-button>
          </div>
        </b-field>
      </div>
    </transition>
  </section>
</template>

<script>
  import AlertModal from '@/components/globals/alert-modal.vue';

  import OrderProviderService from '@/store/classes/OrderProviderService';
  import OrderProviderStoreService from '@/store/classes/OrderProviderStoreService';

  import OrderProviderStoreServiceConfigurationModal from './order-provider-store-service-configuration-modal.vue';
  import AddOrderProviderServiceModal from './add-order-provider-service-modal.vue';

  export default {
    name: 'OrderProviderServicePanel',

    props: {
      store: {
        type: Object,
        required: true
      }
    },

    computed: {
      orderProviderStoreServices() {
        return OrderProviderStoreService
          .query()
          .where('storeId', this.store.storeId)
          .get();
      },

      existingOrderProviderServiceIds() {
        return this.orderProviderStoreServices.map(service => service.orderProviderServiceId);
      }
    },

    async created() {
      this.onCreated();
    },

    methods: {
      async onCreated() {
        await Promise.all([
          this.getOrderProviderServices(),
          this.getOrderProviderStoreServices()
        ]);
      },

      openOrderServiceProviderModal() {
        this.$buefy.modal.open({
          parent: this,
          component: AddOrderProviderServiceModal,
          hasModalCard: true,
          trapFocus: true,
          canCancel: false,
          customClass: 'auto-width',
          props: {
            storeId: this.store.storeId,
            existingOrderProviderServiceIds: this.existingOrderProviderServiceIds
          }
        });
      },

      getOrderProviderServiceDescription(orderProviderServiceId) {
        return OrderProviderService.find(orderProviderServiceId)?.description;
      },

      async getOrderProviderServices() {
        try {
          await OrderProviderService.fetchOrderProviderServices();
        }
        catch (error) {
          this.$_onRequestError({
            toastOptions: {
              message: 'There was a problem fetching the Order Provider Services'
            },
            error
          });
        }
      },

      async getOrderProviderStoreServices() {
        try {
          await OrderProviderStoreService.fetchStoreServices(this.store.storeId);
        }
        catch (error) {
          this.$_onRequestError({
            toastOptions: {
              message: 'There was a problem fetching the Order Provider Store Services'
            },
            error
          });
        }
      },

      confirmRemoveOrderProviderStoreService(storeService) {
        const orderProviderServiceDescription = this.getOrderProviderServiceDescription(storeService.orderProviderServiceId);

        this.$buefy.modal.open({
          parent: this,
          component: AlertModal,
          hasModalCard: true,
          trapFocus: true,
          canCancel: false,
          customClass: 'auto-width',
          props: {
            title: 'Delete Order Provider Service',
            message: `Are you sure you want to delete the <b>${orderProviderServiceDescription}</b> service?`,
            icon: 'trash-alt',
            type: 'is-danger',
            horizontal: true,
            showCloseButton: false,
            buttons: [
              { text: 'Cancel' },
              {
                text: 'Delete',
                primary: true,
                onClick: () => this.removeOrderProviderStoreService(storeService)
              }
            ]
          }
        });
      },

      async toggleActive(storeService) {
        const orderProviderServiceDescription = this.getOrderProviderServiceDescription(storeService.orderProviderServiceId);

        try {
          const payload = {
            ...storeService,
            isActive: !storeService.isActive
          };
          await OrderProviderStoreService.updateStoreService(payload);

          this.$_onRequestSuccess({
            toastOptions: {
              message: `${orderProviderServiceDescription} ${storeService.isActive ? 'deactivated' : 'activated'}!`
            }
          });
        }
        catch (error) {
          this.$_onRequestError({
            error,
            toastOptions: {
              message: `Unable to remove ${orderProviderServiceDescription}`
            }
          });
        }
      },

      openOrderProviderStoreServiceConfigurationModal(storeService) {
        this.$buefy.modal.open({
          parent: this,
          component: OrderProviderStoreServiceConfigurationModal,
          hasModalCard: true,
          trapFocus: true,
          canCancel: false,
          customClass: 'auto-width',
          props: { orderProviderStoreService: storeService }
        });
      },

      async removeOrderProviderStoreService(storeService) {
        try {
          await OrderProviderStoreService.deleteStoreService(storeService.id);

          this.$_onRequestSuccess({
            toastOptions: {
              message: 'Order Provider Service successfully removed!'
            }
          });
        }
        catch (error) {
          this.$_onRequestError({
            error,
            toastOptions: {
              message: 'Unable to remove Order Provider Service'
            }
          });
        }
      }
    }
  };
</script>

<style lang='sass' scoped>
.order-provider-services
  display: grid
  gap: $size-large
  grid-template-columns: repeat(auto-fill, minmax(425px, 1fr))
  grid-auto-rows: 1fr

  .order-provider-store-service
    padding: $size-normal
    border: 1px solid $grey-lightest
    border-radius: $radius
    background-color: $white-bis
</style>
